// Services part 2: ERP/HRMS, UI/UX & Branding, App Support & Maintenance,
// Cloud & DevOps, Growth & Digital Marketing, plus the AI Lab (which lives
// at /ai-lab, not under /services).

export const SERVICES_PART2 = {
  "erp-software-development-company-in-oman": {
    slug: "erp-software-development-company-in-oman",
    path: "/services/erp-software-development-company-in-oman",
    navLabel: "ERP & HRMS",
    eyebrow: "Services",
    h1: "ERP and HRMS Development Company in Oman",
    meta: {
      title: "ERP and HRMS Development Company in Oman",
      description: "Finance, inventory, operations and HR joined up. Local practice built in — Omanisation, WPS, Arabic documents and VAT.",
    },
    intro: "Most businesses here do not need a global ERP. They need finance, inventory, operations and HR joined up, in a system that understands local practice — Omanisation reporting, local leave rules, Arabic documents and VAT.",
    sections: [
      {
        type: "grid", heading: "ERP modules",
        items: [
          { title: "Finance", desc: "GL, AR, AP, banking, VAT-ready." },
          { title: "Inventory", desc: "Stock, movement, valuation across sites." },
          { title: "Purchasing", desc: "Requisitions, POs, approvals, receipts." },
          { title: "Sales", desc: "Quotes to invoices in one thread." },
          { title: "Operations", desc: "The module specific to your business — the reason you're customising." },
          { title: "Reporting", desc: "Managers, analysts and the board see the same numbers." },
        ],
      },
      { type: "callout", text: "Our logistics ERP was built and delivered for a client in Arizona, USA." },
      {
        type: "grid", heading: "HRMS modules",
        items: [
          { title: "Employee records", desc: "Documents, dependents, expiries." },
          { title: "Attendance & leave", desc: "Local leave rules, out-of-office handling." },
          { title: "Payroll", desc: "End-of-service, WPS-ready files." },
          { title: "Recruitment", desc: "Requisition, pipeline, offer letters." },
          { title: "Performance", desc: "Goals, reviews, calibration." },
          { title: "Document expiry alerts", desc: "IDs, licences, insurances — never expire quietly." },
          { title: "Self-service", desc: "Payslips, leave requests, updates." },
          { title: "Omanisation reporting", desc: "Generated, not calculated by hand." },
        ],
      },
      { type: "process" },
      {
        type: "faq",
        items: [
          { q: "Build or buy?", a: "Buy the standard parts, build the part specific to you — we often integrate a standard accounting package with a custom operations module. The best ERP for most businesses is a standard finance system plus a bespoke module for what makes them unique." },
          { q: "How long?", a: "A focused ERP takes three to five months. Full scope runs six to twelve, delivered module by module." },
          { q: "Integrations?", a: "Tally, Zoho Books, QuickBooks, Odoo, SAP — plus banks, VAT portal and WPS." },
          { q: "VAT-ready?", a: "Yes — Omani VAT compliant, with the reports the tax authority requires." },
          { q: "WPS?", a: "Yes, we generate WPS-compliant SIF files for the banks." },
          { q: "Omanisation reporting?", a: "Calculated automatically off the employee record, filed monthly, exported when the Ministry asks." },
        ],
      },
    ],
    related: [
      { tag: "Services", label: "Enterprise software", to: "/services/enterprise-software-development-company-in-oman" },
      { tag: "Solutions", label: "Warehouse management", to: "/solutions/warehouse-management" },
      { tag: "Work", label: "Logistics ERP (Arizona, USA)", to: "/work/logistics-erp" },
    ],
  },

  "ui-ux-design-branding-oman": {
    slug: "ui-ux-design-branding-oman",
    path: "/services/ui-ux-design-branding-oman",
    navLabel: "Design & branding",
    eyebrow: "Services",
    h1: "Design and Branding",
    meta: {
      title: "Design and Branding — Wafa Solutions",
      description: "Product design and branding. Arabic designed, not translated. A design system your engineers can actually build from.",
    },
    intro: "Design is not decoration. It is the difference between a system your staff use and a system your staff avoid. We design products people reach for, and brands that survive the moment they leave the pitch deck.",
    sections: [
      {
        type: "grid", eyebrow: "Product design", heading: "How we design software people use",
        items: [
          { title: "User research", desc: "Interviews and shadowing — with actual users, in their real environment." },
          { title: "Journey mapping", desc: "The whole task, not just the screen." },
          { title: "Wireframes", desc: "Testing the idea before we make it pretty." },
          { title: "High-fidelity design", desc: "Pixel-final screens, in both directions." },
          { title: "Interactive prototypes", desc: "Clickable, testable, share-able." },
          { title: "Design systems", desc: "So the twentieth screen is as consistent as the first." },
          { title: "Usability testing", desc: "Real users, real tasks, timed." },
        ],
      },
      { type: "callout", text: "Arabic is designed, not translated. RTL changes layout, hierarchy, iconography and line length. We design both directions from the start." },
      {
        type: "grid", eyebrow: "Branding", heading: "How we build brands",
        items: [
          { title: "Brand strategy", desc: "Positioning, audience, voice, promise." },
          { title: "Logo & identity", desc: "Marks that work in an app icon and on a truck." },
          { title: "Brand guidelines", desc: "Colour, type, spacing, tone — enforceable." },
          { title: "Collateral", desc: "Decks, proposals, templates your team can actually use." },
          { title: "Digital assets", desc: "For every place the brand shows up online." },
          { title: "Packaging & print", desc: "Right down to the courier bag." },
        ],
      },
      { type: "callout", text: "In build: our marketplace platform has a full brand book covering everything from app icon to courier packaging." },
      { type: "process" },
      {
        type: "faq",
        items: [
          { q: "Do you work on brand as well as product?", a: "Yes — the same team handles both, so your product doesn't fight your brand." },
          { q: "Design system?", a: "Yes, in Figma and mirrored in code so engineers build from tokens, not from screenshots." },
          { q: "Do you do research?", a: "Yes — interviews, shadowing and usability testing. We prefer to test an idea before building it." },
          { q: "Print and packaging?", a: "Yes, we handle print production including packaging and vehicle liveries." },
        ],
      },
    ],
    related: [
      { tag: "Services", label: "Web design & development", to: "/services/web-design-company-in-oman" },
      { tag: "Services", label: "Custom software development", to: "/services/custom-software-development-company-in-oman" },
      { tag: "Services", label: "Growth & digital marketing", to: "/services/digital-marketing-company-in-oman" },
    ],
  },

  "application-support-maintenance-oman": {
    slug: "application-support-maintenance-oman",
    path: "/services/application-support-maintenance-oman",
    navLabel: "Application support & maintenance",
    eyebrow: "Services",
    h1: "Application Support and Maintenance",
    meta: {
      title: "Application Support and Maintenance — Wafa Solutions",
      description: "Support and maintenance with written SLAs. Takeover of systems built by other vendors, after a ten-day audit.",
    },
    intro: "Software is not finished at launch. It is finished when you switch it off. Browsers update, libraries get security patches, your business changes what it needs. Software that nobody maintains does not stay still — it decays.",
    sections: [
      {
        type: "grid", heading: "Our plans",
        items: [
          { title: "Essential", desc: "Next business day. Bug fixes, security patching, uptime monitoring, backup verification." },
          { title: "Business", desc: "Four business hours. Adds minor enhancements and monthly reporting." },
          { title: "Critical", desc: "One hour, 24/7. Adds an on-call engineer and a quarterly review." },
        ],
      },
      {
        type: "block", heading: "Your developer disappeared. The agency stopped answering.",
        paragraphs: [
          "The person who built it left. We take on those situations — after a serious look under the bonnet, not after a phone call.",
        ],
      },
      {
        type: "numbered", heading: "Our takeover process",
        items: [
          "Ten-working-day audit — code, architecture, security, dependencies, deployment.",
          "Written report on code quality, security and risk.",
          "Honest maintain / refactor / rebuild recommendation.",
          "Fixed-price transition — no open-ended discovery.",
          "Then support under a written SLA.",
        ],
      },
      { type: "callout", text: "Sometimes the answer is that the system is fine and you just need someone to watch it." },
      {
        type: "faq",
        items: [
          { q: "Do we have to be your build client?", a: "No. Most of our support clients built their systems with someone else. The audit is designed exactly for those cases." },
          { q: "What if the code is unfixable?", a: "We say so, and we cost a rebuild against the annual maintenance cost of hanging on. Sometimes the honest answer is to rebuild; sometimes it's to keep going for another year." },
          { q: "What does the audit cost?", a: "Fixed price. You get the report either way, whether you continue with us or not." },
          { q: "24/7?", a: "On the Critical plan, yes — one-hour response, on-call engineer, monitored around the clock." },
          { q: "Do you patch security vulnerabilities?", a: "Yes — CVEs on your stack are monitored and patched inside the SLA window on every plan." },
          { q: "Enhancements too?", a: "Small enhancements included on Business and Critical. Larger changes are quoted as fixed-price sprints." },
        ],
      },
    ],
    related: [
      { tag: "Services", label: "Cloud, DevOps & data residency", to: "/services/cloud-devops-oman" },
      { tag: "Services", label: "Custom software development", to: "/services/custom-software-development-company-in-oman" },
      { tag: "Meyar", label: "Meyar Cyber Security GRC", to: "/meyar/cyber-security-grc" },
    ],
  },

  "cloud-devops-oman": {
    slug: "cloud-devops-oman",
    path: "/services/cloud-devops-oman",
    navLabel: "Cloud, DevOps & data residency",
    eyebrow: "Services",
    h1: "Cloud, DevOps and Data Residency",
    meta: {
      title: "Cloud, DevOps and Data Residency — Wafa Solutions",
      description: "Where your data sits is a business decision. We map it with you before the code is written, cost, latency and PDPL impact included.",
    },
    intro: "Where your data sits is a business decision. Most companies never make it — they inherit it. A developer picked a region three years ago, nobody wrote it down, and now personal data is moving across borders nobody has assessed.",
    sections: [
      {
        type: "grid", heading: "What we provide",
        items: [
          { title: "Application hosting & deployment", desc: "Automated, reproducible, monitored." },
          { title: "Managed databases", desc: "PostgreSQL, MongoDB, Redis — sized to load." },
          { title: "Object storage", desc: "Files, media and backups, with lifecycle rules." },
          { title: "Backup & disaster recovery", desc: "Tested, not assumed — see below." },
          { title: "Monitoring & alerting", desc: "So issues wake someone up, not the customer." },
          { title: "DevOps & CI/CD", desc: "Every change goes through the same pipeline." },
          { title: "Security hardening", desc: "OWASP, hardened headers, secret rotation." },
        ],
      },
      { type: "callout", text: "A backup nobody has restored is a rumour." },
      {
        type: "block", heading: "Data residency advisory",
        subheading: "Before we build we map with you:",
        bullets: [
          "What personal data the system holds and which is sensitive",
          "Which component sits where — application, database, files, backups, logs, analytics, third-party tools",
          "What each option costs in money and latency",
          "What each option means for your obligations under the PDPL",
          "What changes if your regulator or largest customer asks a harder question next year",
        ],
      },
      { type: "callout", text: "Then you choose, knowingly." },
      { type: "process" },
      {
        type: "faq",
        items: [
          { q: "Can you migrate us?", a: "Yes — planned, rehearsed, cut over at a time that suits your business. We do the DNS switch on a Sunday morning so nobody notices." },
          { q: "Support a system you did not build?", a: "Yes, after a ten-day audit — see Application support & maintenance." },
          { q: "What if our regulator requires in-country processing?", a: "Then it becomes a hard requirement in the design and we work to it. Tell us at the start — retrofitting residency is one of the most expensive changes there is." },
          { q: "Which cloud providers?", a: "AWS, Google Cloud, Azure and Digital Ocean — chosen for your workload and your residency requirement, not our preference." },
          { q: "How do you handle secrets?", a: "Managed secrets service, rotated on a schedule, never committed. Access is audited." },
          { q: "Disaster recovery drills?", a: "Restored from backup at least every 90 days on Business and Critical support plans. We do it in a staging environment and time it." },
        ],
      },
    ],
    related: [
      { tag: "Services", label: "Application support & maintenance", to: "/services/application-support-maintenance-oman" },
      { tag: "Services", label: "Enterprise software", to: "/services/enterprise-software-development-company-in-oman" },
      { tag: "Meyar", label: "Meyar Cyber Security GRC", to: "/meyar/cyber-security-grc" },
    ],
  },

  "digital-marketing-company-in-oman": {
    slug: "digital-marketing-company-in-oman",
    path: "/services/digital-marketing-company-in-oman",
    navLabel: "Growth & digital marketing",
    eyebrow: "Services",
    h1: "Growth and Digital Marketing",
    meta: {
      title: "Growth and Digital Marketing — Wafa Solutions",
      description: "Acquisition run by the same team that builds your product. We report leads and revenue, not likes.",
    },
    intro: "A great product nobody can find is a hobby. We run acquisition for the same clients we build for, so the people writing your ads understand what your product does — and the landing page they send traffic to is one we can change the same day.",
    sections: [
      {
        type: "grid", heading: "What we run",
        items: [
          { title: "SEO", desc: "Technical, on-page and content. Six-month horizon, honestly explained." },
          { title: "PPC & Google Ads", desc: "Campaign strategy, keyword and audience research, ad copy, testing, budget management." },
          { title: "Social media", desc: "Organic content, paid campaigns, community management." },
          { title: "Content", desc: "Long-form and short-form, English and Arabic, human-written and edited." },
        ],
        columns: 4,
      },
      {
        type: "bullets", heading: "What we will not do",
        items: [
          "Vanity metrics — we report leads and revenue, not likes",
          "Lock-in contracts — month to month after the first three",
          "Black-hat SEO — it works until it destroys you",
          "Promises about rankings — nobody can guarantee position one, and anyone who does is lying to you",
        ],
      },
      { type: "process" },
      {
        type: "faq",
        items: [
          { q: "How long before SEO works?", a: "Three to six months for meaningful movement. Anyone promising faster is selling ads and calling it SEO." },
          { q: "Minimum ad budget?", a: "Useful data starts around OMR 300–500 per month plus management, depending on your industry and geography." },
          { q: "Do you write the content?", a: "Yes — English and Arabic, human writers with an editor above them. No churned AI copy." },
          { q: "Reporting?", a: "Monthly report and a real conversation. We show you leads and revenue, not screenshots of the dashboard." },
          { q: "Lock-in?", a: "Three months to establish a baseline, then month-to-month. If we're not working, we're not the right team." },
        ],
      },
    ],
    related: [
      { tag: "Services", label: "Web design & development", to: "/services/web-design-company-in-oman" },
      { tag: "Services", label: "CRM development", to: "/services/crm-software-development-company-in-oman" },
      { tag: "Services", label: "Design & branding", to: "/services/ui-ux-design-branding-oman" },
    ],
  },

  // AI Lab lives at /ai-lab, not under /services.
  "ai-lab": {
    slug: "ai-lab",
    path: "/ai-lab",
    navLabel: "AI Lab",
    eyebrow: "AI Lab",
    h1: "The AI Lab — AI that survives contact with production",
    meta: {
      title: "The AI Lab — AI that survives production",
      description: "AI built to go into production. Cost ceilings, evaluation loops and a human in the loop where it matters.",
    },
    intro: "Most AI projects die in the pilot. They demo beautifully, then nobody can say what they cost to run, who owns the errors, or how they connect to the system the business actually uses. We build AI that goes into production — evaluation loops, cost ceilings you approve up front, a human in the loop where it matters, and integration into the systems your team already has open.",
    sections: [
      {
        type: "block", tint: "deep",
        eyebrow: "Featured offer",
        heading: "Two-week AI Discovery Sprint",
        subheading: "Fixed price. Fourteen days from kickoff to a working prototype.",
        paragraphs: [
          "You get: a use-case map scored by value and effort, a working prototype against your real data, a cost model per 1,000 uses, and a fixed-price rollout quote.",
          "If the answer is \"AI is the wrong tool for this\", we tell you that instead — and you have spent two weeks, not two quarters.",
        ],
      },
      {
        type: "grid", heading: "What we build",
        items: [
          { title: "Conversational AI", desc: "Bots that understand Arabic, know when to hand off, and log every conversation." },
          { title: "Document intelligence", desc: "Invoices, POs, forms — read what a person would." },
          { title: "AI copilots inside existing systems", desc: "Meyar does exactly this — an assistant on every risk measure." },
          { title: "Agentic workflows", desc: "With approval gates for anything touching money or a legal obligation." },
          { title: "Predictive models", desc: "Churn, demand, anomaly detection — with a confidence band, not a magic number." },
          { title: "Arabic-first AI", desc: "Models chosen and evaluated on Arabic tasks, not English retrofitted." },
        ],
      },
      {
        type: "block", heading: "How we keep AI honest",
        bullets: [
          "Cost ceilings modelled before we build and capped in code",
          "Evaluation loops measured against a real test set, not vibes",
          "Human in the loop for anything touching money, safety or a legal obligation",
          "No black boxes — every decision traceable",
          "Your data stays yours, no training on it",
        ],
      },
      { type: "process" },
      {
        type: "faq",
        items: [
          { q: "Do we need our own data?", a: "For most use cases, yes — that's what makes the AI useful for your business, rather than a generic assistant. We work with the data you already have." },
          { q: "Will our data train a model?", a: "No. Your data stays yours. We use provider APIs where contracts guarantee no training on your data, or we run models where you control the environment." },
          { q: "Which models?", a: "Whichever fits — GPT, Claude, Gemini, open-source. We'll tell you when a cheaper model does the job. Model choice is engineering, not brand loyalty." },
          { q: "Where does it run?", a: "We map it with you before we build — provider region, self-hosted, or hybrid. See our Cloud & data residency service for how we approach this." },
          { q: "What if AI is not the right answer?", a: "We say so in week one. Sometimes a well-configured rule engine, or a plain database query, is what you actually needed." },
          { q: "Costs?", a: "We model cost per 1,000 uses before we build, then cap it in code. You will not be surprised by an inference bill." },
        ],
      },
    ],
    related: [
      { tag: "Services", label: "Custom software development", to: "/services/custom-software-development-company-in-oman" },
      { tag: "Services", label: "CRM development", to: "/services/crm-software-development-company-in-oman" },
      { tag: "Meyar", label: "Meyar overview", to: "/meyar" },
    ],
  },
};
